import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Megaphone, PenTool, Landmark, BadgeCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Pill, Loader } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const field = "w-full h-11 bg-white/[0.04] border border-white/10 px-4 text-sm outline-none focus:border-neon/60 transition-colors";

const TRACKS = [
  { id: "policy_research", label: "Policy Research", icon: Landmark, blurb: "Dig into bills, budgets and city data. Turn dense documents into arguments people can act on." },
  { id: "community_organizing", label: "Community Organizing", icon: Megaphone, blurb: "Run local circles, town halls and signature drives. Build the muscle on the ground." },
  { id: "digital_content", label: "Digital / Content", icon: PenTool, blurb: "Explainers, reels, ground reports. Make civic issues impossible to scroll past." },
  { id: "campus_ambassador", label: "Campus Ambassador", icon: GraduationCap, blurb: "Represent People's Circle in your college. Host debates, recruit contributors, lead campaigns." },
];
const trackLabel = (id: string) => TRACKS.find((t) => t.id === id)?.label || id;

const PERKS = [
  "Fellow badge on your profile across the platform",
  "Direct mentorship from organizers and policy researchers",
  "Priority passes to People's Circle events and closed-door sessions",
  "Certificate + letter of recommendation on completing the cohort",
];

const Fellowship = () => {
  const { user, login, refresh } = useAuth();
  const [fellows, setFellows] = useState<any[]>([]);
  const [mine, setMine] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ age: "", city: "", track: "policy_research", motivation: "", experience: "" });

  useEffect(() => {
    api.get("/fellowship/fellows").then((r) => setFellows(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (user) api.get("/fellowship/me").then((r) => setMine(r.data)).catch(() => {});
  }, [user]);

  const apply = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/fellowship/apply", {
        age: Number(f.age), city: f.city, track: f.track,
        motivation: f.motivation, experience: f.experience || undefined,
      });
      setMine(data);
      toast.success("Application submitted. We'll email you the decision.");
      refresh().catch(() => {});
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="fellowship-page">
      {/* hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24">
          <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">Under 35 · Any city · Fully remote-friendly</span>
          <h1 className="mt-4 font-bebas text-6xl sm:text-7xl lg:text-8xl tracking-wide uppercase leading-[0.9] max-w-4xl">
            Youth Fellowship<span className="text-neon">.</span><br />Six months to move your city.
          </h1>
          <p className="mt-6 text-base text-white/55 max-w-2xl">
            A hands-on program for people aged 15–35 who want to do civic work, not just talk about it.
            Pick a track, get a mentor, run real campaigns with the circle behind you.
          </p>
          <a href="#apply" data-testid="fellowship-hero-apply" className="mt-10 inline-block bg-neon px-10 h-13 leading-[52px] text-[13px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow">
            Apply now
          </a>
        </div>
      </section>

      {/* tracks */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20">
        <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">Four tracks</span>
        <h2 className="mt-3 font-bebas text-4xl sm:text-5xl tracking-wide uppercase">Pick your lane</h2>
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {TRACKS.map((t, i) => (
            <div key={t.id} className="border border-white/10 bg-ink-surface p-7 hover:border-neon/40 transition-colors group" data-testid={`fellowship-track-${t.id}`}>
              <t.icon className="text-neon" size={26} />
              <h3 className="mt-5 font-outfit text-lg font-bold group-hover:text-neon transition-colors">{t.label}</h3>
              <p className="mt-2 text-sm text-white/50">{t.blurb}</p>
              <div className="mt-5 font-bebas text-4xl text-white/10">0{i + 1}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 grid gap-10 lg:grid-cols-2 items-start">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">What you get</span>
            <h2 className="mt-3 font-bebas text-4xl sm:text-5xl tracking-wide uppercase">Fellow perks</h2>
            <ul className="mt-8 grid gap-4">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-white/65">
                  <BadgeCheck size={17} className="text-pcblue shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
          </div>

          {/* apply */}
          <div id="apply" className="border border-white/10 bg-ink-surface p-8" data-testid="fellowship-apply-card">
            <h2 className="font-bebas text-4xl tracking-wide uppercase">Apply for the cohort</h2>
            {!user ? (
              <>
                <p className="mt-3 text-sm text-white/50">Sign in to submit your application.</p>
                <button onClick={login} data-testid="fellowship-login-btn" className="mt-6 h-11 w-full bg-neon text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow">Join the circle</button>
              </>
            ) : mine && mine.status !== "none" && mine.status !== "rejected" ? (
              <div className="mt-5" data-testid="fellowship-status">
                <Pill tone={mine.status === "accepted" ? "blue" : "yellow"}>{mine.status}</Pill>
                <p className="mt-4 text-sm text-white/55">
                  {mine.status === "accepted"
                    ? "You're in. Welcome, Fellow — check your email for next steps."
                    : "Your application is with the review team. We'll email you the decision."}
                </p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-white/35">Track: {trackLabel(mine.track)}</p>
              </div>
            ) : (
              <form onSubmit={apply} className="mt-6 grid gap-4" data-testid="fellowship-form">
                {mine?.status === "rejected" && (
                  <p className="text-sm text-pcyellow">Your last application wasn&apos;t selected. You can apply again.</p>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <input required type="number" min={15} max={35} placeholder="Age (15–35)" value={f.age} onChange={(e) => setF({ ...f, age: e.target.value })} data-testid="fellowship-age-input" className={field} />
                  <input required placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="fellowship-city-input" className={field} />
                </div>
                <select value={f.track} onChange={(e) => setF({ ...f, track: e.target.value })} data-testid="fellowship-track-select" className={field}>
                  {TRACKS.map((t) => <option key={t.id} value={t.id} className="bg-ink-surface">{t.label}</option>)}
                </select>
                <textarea required minLength={50} rows={5} placeholder="Why do you want this fellowship? What issue keeps you up at night? (min 50 chars)" value={f.motivation} onChange={(e) => setF({ ...f, motivation: e.target.value })} data-testid="fellowship-motivation-input" className="w-full bg-white/[0.04] border border-white/10 p-4 text-sm outline-none focus:border-neon/60 transition-colors" />
                <textarea rows={3} placeholder="Any past civic / volunteer / campus work? (optional)" value={f.experience} onChange={(e) => setF({ ...f, experience: e.target.value })} data-testid="fellowship-experience-input" className="w-full bg-white/[0.04] border border-white/10 p-4 text-sm outline-none focus:border-neon/60 transition-colors" />
                <button data-testid="fellowship-submit-btn" className="h-11 bg-neon text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow">Submit application</button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* fellows wall */}
      <section className="border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20">
          <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">The wall</span>
          <h2 className="mt-3 font-bebas text-4xl sm:text-5xl tracking-wide uppercase">Current fellows</h2>
          {loading ? (
            <Loader />
          ) : fellows.length === 0 ? (
            <p className="mt-8 text-sm text-white/40" data-testid="fellows-empty">The first cohort is being selected. Your name could be here.</p>
          ) : (
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {fellows.map((fl, i) => (
                <div key={i} className="border border-white/10 bg-ink-surface p-5 flex items-center gap-4" data-testid={`fellow-card-${i}`}>
                  {fl.user_picture ? (
                    <img src={fl.user_picture} alt={fl.user_name} className="w-11 h-11 rounded-full border border-white/15" />
                  ) : (
                    <span className="w-11 h-11 grid place-items-center bg-neon font-bebas text-xl shrink-0">{fl.user_name[0]}</span>
                  )}
                  <div className="min-w-0">
                    <div className="font-outfit font-semibold truncate">{fl.user_name}</div>
                    <div className="text-[11px] uppercase tracking-[0.14em] text-white/40">{trackLabel(fl.track)} · {fl.city}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Fellowship;
